import { CircularProgress } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactSelect, { SingleValue } from "react-select";

import FormatGroupLabel from "components/FormatGroupLabel";
import PaymentInfo from "components/PaymentInfo";
import QuoteStatusInfo from "components/QuoteStatusInfo";
import { useAuth } from "providers/Auth";
import { useQuoteDetails } from "providers/QuoteDetails";
import { api } from "services";
import {
  PaymentMethod,
  QuoteActions,
  QuoteStatus,
  quoteStatusColor,
  quoteStatusTitle,
  RoleDescription,
} from "types/enums";
import { GroupedOption, Quote, StatusOption } from "types/quotes";
import { formatPrice } from "utils/formats";
import { toast } from "utils/toast";

import * as C from "./styles";

interface ProfessionalQuoteCardProps {
  quote: Quote;
}

const ProfessionalQuoteCard = ({ quote }: ProfessionalQuoteCardProps) => {
  const { userRole } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { pendingItems } = useQuoteDetails();

  const [prevStepOptions, setPrevStepOtions] = useState(
    quote?.previusStep?.map((step) => ({
      value: step,
      label: quoteStatusTitle.get(step) as string,
      statusColor: `var(--${quoteStatusColor.get(step)})`,
    }))
  );

  const [nextStepOptions, setNextStepOtions] = useState(
    quote?.nextStep?.map((step) => ({
      value: step,
      label: quoteStatusTitle.get(step) as string,
      statusColor: `var(--${quoteStatusColor.get(step)})`,
    }))
  );

  const [selected, setSelected] = useState<StatusOption>({
    value: quote.quoteStatus,
    label: quoteStatusTitle.get(quote.quoteStatus) as string,
    statusColor: `var(--${quoteStatusColor.get(quote.quoteStatus)})`,
  });

  const customStyles = (value: StatusOption) => ({
    container: (base: Record<string, unknown>) => ({
      ...base,
      padding: 0,
      display: "inline-block",
    }),
    control: (base: Record<string, unknown>) => ({
      ...base,
      minHeight: "unset",
      height: 26,
      background: value?.statusColor,
      border: "none",
      borderRadius: "2rem",
      transition: "all 0.2s",
      boxShadow: "none",
      cursor: value.value === QuoteStatus.Finished ? "default" : "pointer",
      padding: "0 4px",
      "&:hover": {
        filter:
          value.value === QuoteStatus.Finished ? "none" : "brightness(1.1)",
      },
    }),
    menu: (base: Record<string, unknown>) => ({
      ...base,
      width: 145,
      background: "var(--white)",
      color: "var(--text)",
      border: "1px solid var(--border)",
      borderRadius: "0.5rem",
      boxShadow: "var(--shadow)",
      zIndex: 10,
    }),
    option: (base: Record<string, unknown>, state: any) => ({
      ...base,
      cursor: "pointer",
      fontWeight: 700,
      fontSize: "0.8rem",
      color: state.data.statusColor,
      backgroundColor: state.isSelected ? "var(--light)" : "transparent",
      "&:hover": {
        backgroundColor: "var(--light)",
      },
    }),
    valueContainer: (base: Record<string, unknown>) => ({
      ...base,
      padding: "0 2px",
    }),
    singleValue: (base: Record<string, unknown>) => ({
      ...base,
      fontWeight: 700,
      fontSize: "0.75rem",
      color: "#fff",
      padding: "0 2px",
      letterSpacing: "0.03em",
      textTransform: "uppercase" as const,
    }),
    indicatorsContainer: (base: Record<string, unknown>) => ({
      ...base,
      display: value.value === QuoteStatus.Finished ? "none" : "flex",
    }),
    dropdownIndicator: (base: Record<string, unknown>) => ({
      ...base,
      padding: "0 2px",
      "& svg": {
        width: 13,
        color: "rgba(255,255,255,0.85)",
      },
    }),
  });

  const changeStatus = async (
    newStatus: SingleValue<{
      value: number;
      label: string;
      statusColor: string;
    }>
  ) => {
    setIsLoading(true);
    setSelected(newStatus as StatusOption);
    try {
      await api.patch(`/Quote/${quote.id}/status`, null, {
        params: { newStatus: newStatus?.value },
      });
      const response = await api.get(`/Quote/${quote.id}`);
      setIsLoading(false);
      setPrevStepOtions(
        response.data.previusStep?.map((step: number) => ({
          value: step,
          label: quoteStatusTitle.get(step),
          statusColor: `var(--${quoteStatusColor.get(step)})`,
        }))
      );
      setNextStepOtions(
        response.data.nextStep?.map((step: number) => ({
          value: step,
          label: quoteStatusTitle.get(step),
          statusColor: `var(--${quoteStatusColor.get(step)})`,
        }))
      );
      toast.fire({ icon: "success", title: "Status atualizado com sucesso!" });
    } catch {
      setIsLoading(false);
      toast.fire({ icon: "error", title: "Ocorreu um erro, tente novamente." });
    }
  };

  const groupedOptions: readonly GroupedOption[] = [
    { label: "Retornar para:", options: prevStepOptions },
    { label: "Avançar para:", options: nextStepOptions },
  ];

  const isFinancing = quote.payment?.method === PaymentMethod.Financing;
  const statusColorVar = `var(--${quoteStatusColor.get(quote.quoteStatus)})`;
  const hasPending = !!pendingItems.length;

  const hasAnyFile = quote.hasAttachments;
  const docsState = quote.hasAllDocumention
    ? "complete"
    : hasAnyFile
    ? "partial"
    : "none";

  return (
    <C.Card
      style={{ "--status-color": statusColorVar } as React.CSSProperties}
      aria-label={`Orçamento #${quote.id} — ${quote.patient.name}`}
    >
      {/* ── Header ── */}
      <C.CardHeader>
        <C.QuoteId>#{quote.id}</C.QuoteId>

        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{quote.patient.name}</Tooltip>}
        >
          <C.PatientName>{quote.patient.name}</C.PatientName>
        </OverlayTrigger>

        <C.StatusWrapper>
          {isLoading && (
            <CircularProgress size={12} color="inherit" aria-label="Atualizando status" />
          )}
          {quote.actions.includes(QuoteActions.EditStatus) ? (
            <ReactSelect
              styles={customStyles(selected)}
              options={groupedOptions}
              value={selected}
              onChange={changeStatus}
              isSearchable={false}
              formatGroupLabel={FormatGroupLabel}
              noOptionsMessage={() => null}
              aria-label="Alterar status do orçamento"
            />
          ) : (
            <QuoteStatusInfo
              quoteStatus={quote.quoteStatus}
              paymentMethod={quote.payment?.method}
            />
          )}
        </C.StatusWrapper>

        {docsState !== "none" && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {docsState === "complete"
                  ? "Toda a documentação foi enviada e aprovada"
                  : "Documentos anexados, aguardando aprovação"}
              </Tooltip>
            }
          >
            <C.DocsBadge
              $state={docsState}
              aria-label={
                docsState === "complete"
                  ? "Documentação completa"
                  : "Documentos anexados"
              }
            >
              <i
                className={
                  docsState === "complete"
                    ? "uil uil-check-circle"
                    : "uil uil-paperclip"
                }
                aria-hidden="true"
              />
              {docsState === "complete" ? "Documentado" : "Docs anexados"}
            </C.DocsBadge>
          </OverlayTrigger>
        )}

        <C.DetailsButton
          as={Link}
          to="/orcamentos-profissional/orcamento-detalhes"
          state={quote.id}
          className={hasPending ? "has-pending" : ""}
          aria-label={
            hasPending
              ? `Orçamento #${quote.id} tem pendências`
              : `Ver detalhes do orçamento #${quote.id}`
          }
        >
          {hasPending ? (
            <>
              <i className="uil uil-exclamation-circle" aria-hidden="true" />
              Pendências
            </>
          ) : (
            <>
              <i className="uil uil-arrow-right" aria-hidden="true" />
              Detalhes
            </>
          )}
        </C.DetailsButton>
      </C.CardHeader>

      {/* ── Metadata Grid ── */}
      <C.CardBody aria-label="Informações do orçamento">
        <C.MetaItem>
          <C.MetaLabel>Profissional</C.MetaLabel>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {quote.professional?.name ?? "Não informado"}
              </Tooltip>
            }
          >
            {quote.professional?.name ? (
              <C.MetaValue>{quote.professional.name}</C.MetaValue>
            ) : (
              <C.MetaValueMuted>Não informado</C.MetaValueMuted>
            )}
          </OverlayTrigger>
          {quote.company?.name && (
            <C.MetaSub>{quote.company.name}</C.MetaSub>
          )}
        </C.MetaItem>

        <C.MetaItem>
          <C.MetaLabel>Criação</C.MetaLabel>
          <C.MetaValue>
            {moment(quote.creationDate).format("DD/MM/YYYY")}
          </C.MetaValue>
        </C.MetaItem>

        <C.MetaItem>
          <C.MetaLabel>Procedimento</C.MetaLabel>
          <C.MetaValue>
            {moment(quote.date).format("DD/MM/YYYY")}
          </C.MetaValue>
        </C.MetaItem>

        <C.MetaItem>
          <C.MetaLabel>Limite</C.MetaLabel>
          {quote.hasLimitDateAlert ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Orçamento está prestes a expirar</Tooltip>}
            >
              <C.MetaValueDanger>
                <i className="uil uil-exclamation-triangle" aria-hidden="true" />
                {moment(quote.limitDate).format("DD/MM/YY")}
              </C.MetaValueDanger>
            </OverlayTrigger>
          ) : (
            <C.MetaValue>
              {moment(quote.limitDate).format("DD/MM/YYYY")}
            </C.MetaValue>
          )}
        </C.MetaItem>

        <C.MetaItem>
          <C.MetaLabel>
            Valor <PaymentInfo payment={quote.payment} />
          </C.MetaLabel>
          <C.MetaValuePrimary>
            {quote.payment ? formatPrice(quote.payment.value) : "—"}
          </C.MetaValuePrimary>
        </C.MetaItem>

        {userRole === RoleDescription.Admin && !!quote.payment?.orderId && (
          <C.MetaItem>
            <C.MetaLabel>Pagamento</C.MetaLabel>
            <C.MetaValue>#{quote.payment.orderId}</C.MetaValue>
          </C.MetaItem>
        )}
      </C.CardBody>

      {/* ── Footer ── */}
      <C.CardFooter>
        <C.PaymentBadge $dark={isFinancing}>
          <i
            className={`uil ${
              isFinancing ? "uil-files-landscapes-alt" : "uil-credit-card"
            }`}
            aria-hidden="true"
          />
          {isFinancing ? "Financiamento" : "Parcelamento"}
        </C.PaymentBadge>

        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {quote.hasTerm ? "Termo assinado" : "Termo não assinado"}
            </Tooltip>
          }
        >
          <C.DocIndicator
            $active={quote.hasTerm}
            aria-label={quote.hasTerm ? "Termo assinado" : "Termo não assinado"}
          >
            <i className="uil uil-file-edit-alt" aria-hidden="true" />
            Termo
          </C.DocIndicator>
        </OverlayTrigger>

        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {quote.hasAllDocumention
                ? "Documentação completa e aprovada"
                : hasAnyFile
                ? "Documentos anexados — aguardando aprovação"
                : "Nenhum documento enviado"}
            </Tooltip>
          }
        >
          <C.DocIndicator
            $active={quote.hasAllDocumention}
            $partial={hasAnyFile && !quote.hasAllDocumention}
            aria-label={
              quote.hasAllDocumention
                ? "Documentação aprovada"
                : hasAnyFile
                ? "Documentos anexados"
                : "Sem documentos"
            }
          >
            <i
              className={
                quote.hasAllDocumention
                  ? "uil uil-check-circle"
                  : hasAnyFile
                  ? "uil uil-paperclip"
                  : "uil uil-file-upload"
              }
              aria-hidden="true"
            />
            Docs
          </C.DocIndicator>
        </OverlayTrigger>

        {quote.hasCheckList && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {quote.doneCheckList
                  ? "Checklist completo"
                  : "Checklist incompleto"}
              </Tooltip>
            }
          >
            <C.DocIndicator
              $active={quote.doneCheckList}
              aria-label={
                quote.doneCheckList
                  ? "Checklist completo"
                  : "Checklist incompleto"
              }
            >
              <i className="uil uil-check-circle" aria-hidden="true" />
              Checklist
            </C.DocIndicator>
          </OverlayTrigger>
        )}

        {hasAnyFile && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Orçamento possui arquivos anexados</Tooltip>}
          >
            <C.DocIndicator
              $active
              aria-label="Possui anexos"
            >
              <i className="uil uil-paperclip" aria-hidden="true" />
              Anexo
            </C.DocIndicator>
          </OverlayTrigger>
        )}
      </C.CardFooter>
    </C.Card>
  );
};

export default ProfessionalQuoteCard;
